import React, { useEffect, useState, useMemo } from "react";
import { Typography, Button, Grid } from "@material-ui/core";
import translate from "../i18n/Translator";
import DialogPopup from "../components/DialogPopup";
import { SyncCfdisSatVaultRequest } from "../model/SatVault";
import DateRange from "../components/DateRange";
import { WarningSnackbar } from "../components/Snackbars";
import { syncManualVaultSat } from "../api/SupportAPI";
import DateRangeInput from "../components/DateRangeInput";

interface SyncCfdisVaultSatPopUpProps {
  tenantId: string;
  tenantName: string;
  onClose(totalSync?: number, ): any;
}

export default function SyncCfdisVaultSatPopUp(
  props: SyncCfdisVaultSatPopUpProps
) {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const maskStartDate = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const [sDate, setSDate] = useState<Date>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 6
    )
  );
  const [eDate, setEDate] = useState<Date>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    )
  );

  const [request, setRequest] = useState<SyncCfdisSatVaultRequest>({
    issued_from: maskStartDate(sDate),
    issued_to:maskStartDate(eDate),
    tenant_id: props.tenantId,
  } as SyncCfdisSatVaultRequest);

  const initialPeriod = useMemo(() => {
    const formatDate = (date: Date) => {
      return (
        String(date.getDate()).padStart(2, "0") +
        "/" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "/" +
        date.getFullYear()
      );
    };
    return `${formatDate(sDate)} - ${formatDate(eDate)}`;
  }, []);

  const [period, setPeriod] = useState<string>(initialPeriod);

  const [warning, setWarning] = useState<string>();

  useEffect(() => {
    // eslint-disable-next-line
  }, [props]);

  const onSync = () => {
    setSubmitting(true);
    if (!request || !request.issued_from || !request.issued_to) {
      setSubmitting(false);
      return;
    }

    if (hasInvalidRange(sDate, eDate)) {
      setWarning(
        translate(`connectors.download_by_range.range_limit`, {
          dias: 7 + "",
        }) as string
      );
      setSubmitting(false);
      return;
    }

    syncManualVaultSat(request)
      .then((response) => {
        props.onClose(response.total);
      })
      .catch((error) => {
        setWarning(error.message);
      });
  };

  const hasInvalidRange = (startDate: Date, endDate: Date) => {
    let difference = endDate.getTime() - startDate.getTime();
    let daysBetween = Math.round(difference / (1000 * 60 * 60 * 24));
    return daysBetween !== undefined && daysBetween > 7;
  };

  const onChangeDateRange = (
    startDate?: Date,
    endDate?: Date,
    formatted?: string
  ) => {
    if (!startDate || !endDate || !formatted) {
      return;
    }

    let invalidRange = hasInvalidRange(startDate, endDate);

    if (invalidRange) {
      setWarning(
        translate(`connectors.download_by_range.range_limit`, {
          dias: 7 + "",
        }) as string
      );
    } else {
      setRequest({
        ...request,
        tenant_id: props.tenantId,
        issued_from: maskStartDate(startDate),
        issued_to: endDate ? maskStartDate(endDate) : startDate,
      });
    }
    setSDate(startDate);
    setEDate(endDate);
    setPeriod(formatted);
  };

  const buttonSync = () => {
    return (
      <Button
        onClick={onSync}
        disabled={hasInvalidRange(sDate, eDate)}
        variant="contained"
        color="primary"
      >
        {translate("buttons.sync") as string}
      </Button>
    );
  };

  const onCloseSnackbars = () => {
    setWarning(undefined);
  };

  return (
    <DialogPopup
      open
      title={translate("sync_cfdis.title") as string}
      onClose={props.onClose}
      closeColor="default"
      button={buttonSync()}
      disableEscapeKeyDown={submitting}
      disableBackdropClick={submitting}
      closeText={translate("buttons.cancel") as string}
    >
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="body2" paragraph>
            {
              translate("sync_cfdis.message", {
                tenant_name: props.tenantName,
              }) as string
            }
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DateRangeInput
            id="period"
            label={
              translate("cfdis.cfdis_non_integrated.filters.period") as string
            }
            value={period}
            onChange={onChangeDateRange}
            disabled={false}
            maxDate={new Date()}
            margin="dense"
            tooltip={
              translate(
                "cfdis.cfdis_non_integrated.tooltips.period_selection"
              ) as string
            }
          />
        </Grid>
      </Grid>

      <WarningSnackbar message={warning} onClose={onCloseSnackbars} />
    </DialogPopup>
  );
}
